import { zipSync, type Zippable } from "fflate";
import type { LiveFile } from "./liveTypes";

/**
 * "Download all" on the client's Kit: every file of the post AND every video's
 * cover, in one zip made in the browser (no backend). The URLs are short-lived
 * signed GETs on our storage bucket, whose CORS allows the app's origins, so
 * they can be fetched and zipped here. Stored, not compressed: images and
 * videos are compressed already, and level 0 keeps a big video from freezing
 * the tab.
 *
 * A file that cannot be fetched (CORS on another bucket, an expired link, the
 * network) is not lost: it is handed to the browser to download on its own,
 * one after another.
 */

/** One thing the zip will hold: a file of the post, or a video's cover. */
export interface KitDownload {
  url: string;
  /** Without an extension when the source name has none; the response type supplies it. */
  name: string;
}

const EXTENSION_BY_TYPE: Record<string, string> = {
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "video/webm": "webm",
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
};

/** "launch_9x16.mp4" → "launch_9x16 cover". */
function coverName(videoName: string): string {
  return `${videoName.replace(/\.[a-z0-9]{2,5}$/i, "")} cover`;
}

/**
 * What "Download all" saves: every file on the post with a link and not still
 * uploading, plus each video's cover. A video's cover lives on the video's own
 * row (`thumbUrl`) — the Kit's separate "The cover" row has no download link of
 * its own — so it is taken from there. Deduplicated by URL, so a cover that is
 * also uploaded as its own picture is saved once.
 */
export function kitDownloads(files: LiveFile[]): KitDownload[] {
  const seen = new Set<string>();
  const out: KitDownload[] = [];
  const add = (url: string | null, name: string) => {
    if (!url || seen.has(url)) return;
    seen.add(url);
    out.push({ url, name });
  };
  for (const file of files) {
    if (file.status === "uploading" || file.status === "failed") continue;
    add(file.mediaUrl, file.name);
    if (file.kind === "VIDEO") add(file.thumbUrl, coverName(file.name));
  }
  return out;
}

function safeName(raw: string): string {
  const cleaned = raw.replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim();
  return cleaned || "file";
}

/** `name` with an extension (from the response type when it has none), unique in `taken`. */
function entryName(name: string, contentType: string | null, taken: Set<string>): string {
  let base = safeName(name);
  if (!/\.[a-z0-9]{2,5}$/i.test(base)) {
    const ext = contentType ? EXTENSION_BY_TYPE[contentType.split(";")[0].trim().toLowerCase()] : undefined;
    if (ext) base = `${base}.${ext}`;
  }
  let candidate = base;
  for (let n = 2; taken.has(candidate.toLowerCase()); n += 1) {
    candidate = base.replace(/(\.[^.]+)?$/, (ext) => ` (${n})${ext}`);
  }
  taken.add(candidate.toLowerCase());
  return candidate;
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Long enough for the browser to start the download of a large file.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** The browser's own download of a link we could not fetch (it may open in a tab instead). */
function openLink(url: string, filename: string) {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.target = "_blank";
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

const pause = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

export interface KitDownloadResult {
  /** Files that went into the zip (or downloaded as the one file). */
  saved: number;
  /** Files handed to the browser one by one because they could not be fetched. */
  separately: number;
}

export async function downloadKit(files: LiveFile[], archiveName: string): Promise<KitDownloadResult> {
  const list = kitDownloads(files);
  const fetched = await Promise.allSettled(
    list.map(async (item) => {
      const response = await fetch(item.url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return { item, type: response.headers.get("content-type"), bytes: new Uint8Array(await response.arrayBuffer()) };
    }),
  );

  const taken = new Set<string>();
  const ok: Array<{ name: string; type: string | null; bytes: Uint8Array }> = [];
  const failed: KitDownload[] = [];
  fetched.forEach((result, index) => {
    if (result.status === "fulfilled") {
      ok.push({ name: entryName(result.value.item.name, result.value.type, taken), type: result.value.type, bytes: result.value.bytes });
    } else {
      failed.push(list[index]);
    }
  });

  if (ok.length === 1 && failed.length === 0) {
    saveBlob(new Blob([ok[0].bytes as BlobPart], { type: ok[0].type ?? "application/octet-stream" }), ok[0].name);
  } else if (ok.length > 0) {
    const entries: Zippable = {};
    for (const entry of ok) entries[entry.name] = [entry.bytes, { level: 0 }];
    const zipped = zipSync(entries);
    saveBlob(new Blob([zipped as BlobPart], { type: "application/zip" }), `${safeName(archiveName)}.zip`);
  }

  // One at a time: browsers drop a burst of downloads started in the same tick.
  for (const item of failed) {
    openLink(item.url, safeName(item.name));
    await pause(400);
  }

  return { saved: ok.length, separately: failed.length };
}
